// ---- The vertical solver (gravity + jumping), pure and shared ----------------
// ONE implementation of vertical motion for every mover: the local player
// (game.ts), the server's movement envelope (src/server/handlers/movement.ts),
// the studio Skill Sim driver, and — via verticalPass in sim/combat.ts — NPCs.
// Replaces the old cosmetic jump (an additive render offset that never touched
// collision) with a real state machine: grounded movers track the standable
// surface under them (stairs step up, small drops glide), real drops and jumps
// go BALLISTIC (integrated vy), and landing picks the highest standable surface
// the arc actually crossed — which is what lets you jump ONTO tables, rocks,
// roofs, and wall tops, or off a cliff.
//
// Purity rules (same tier as sim/combat.ts, stricter than physics.ts): no
// imports AT ALL — no THREE, no DOM, no Bun, no terrain/physics. Geometry
// arrives through the `stand` callback (the physics.ts standAtWith family:
// "highest standable surface at (x,z) within step-up reach of refY, or null");
// side effects leave as the returned VertEvents. Deterministic: no Date.now,
// no Math.random — the same inputs always produce the same flight.
//
// The `stand(x, z, refY) === null` contract (see physics.ts standAtWith):
// null means NOTHING standable is within reach — which, because terrain BELOW
// you is always within reach, only happens when the terrain surface towers
// ABOVE refY + stepUp (a cliff face / the inside of a hill). So:
//   - grounded + null  → HOLD height (never adopt a wall; the slope gate and
//     colliders own pushing the mover back out — freefalling here would drop
//     the mover through the world from inside the hill).
//   - airborne + null  → keep falling (sailing past a cliff face is normal).
// Walking off a ledge is NOT the null case: the ground far below is "in reach"
// (below always is), it's just a big drop — the dropFreefall threshold decides
// glide vs. ballistic.

/** Tuning defaults. The live values everywhere — client, server envelope, and
 *  the Skill Sim — so the prediction and the validator can never disagree.
 *  Pinned by vertical.test.ts; `stepUp` mirrors physics.STEP_UP (the sim can't
 *  import physics.ts — the parity test asserts they stay equal). */
export const V = {
  /** Downward acceleration (units/sec²). */
  gravity: 30,
  /** Initial jump velocity (units/sec). Apex = jumpVel²/(2·gravity) ≈ 1.30u —
   *  enough to mount tables/chests/rocks/fences, not walls (~2.5u+). */
  jumpVel: 8.85,
  /** Tallest lip a grounded mover steps straight up (= physics.STEP_UP). */
  stepUp: 0.7,
  /** A grounded drop deeper than this goes ballistic; shallower drops keep the
   *  classic glide so ordinary downhill walking feels unchanged. */
  dropFreefall: 1.3,
  /** Small-drop glide rate (the historical `dy * min(1, dt*10)` ease). */
  glideRate: 10,
  /** Jump grace after walking off a ledge (seconds). */
  coyote: 0.1,
  /** How early a jump press may queue before landing (seconds). */
  jumpBuffer: 0.1,
  /** Terminal fall speed (units/sec). */
  maxFallVy: 40,
  /** Falls up to this far land clean; beyond it, fall damage scales. */
  safeFall: 4.5,
  /** HP per unit fallen past safeFall (server-side, on landing). */
  dmgPerUnit: 3,
  /** Terrain rise/run beyond which walking uphill is blocked (≈54.5°) — the
   *  slope gate consumed by physics.slopeGateWith call sites. */
  maxSlope: 1.4,
  /** Movement speed multiplier while wading (feet below the shoreline). */
  wadeSlow: 0.45,
  /** Horizontal speed multiplier while airborne. 1 = full air control (the
   *  shipped feel); lower it for floatier, commit-to-the-jump arcs. Client
   *  feel only — the server speed budget is an upper bound, so any value ≤ 1
   *  validates unchanged. */
  airCtl: 1,
};

export type VertTuning = typeof V;

/** One mover's vertical state. Height (`y`) is the FEET. */
export type VertState = {
  y: number;
  /** Vertical velocity (units/sec; negative = falling). 0 while grounded. */
  vy: number;
  /** Ballistic (jumping or falling)? False = tracking the ground. */
  air: boolean;
  /** Seconds since last grounded — the coyote-jump window. A jump sets it past
   *  the window (Infinity) so airtime can never chain into a double jump. */
  coyoteT: number;
  /** Seconds since the last jump press (Infinity = none pending) — buffering. */
  bufT: number;
  /** Highest y of the current flight — landing fall-distance accounting. */
  peakY: number;
};

export function freshVert(y = 0): VertState {
  return { y, vy: 0, air: false, coyoteT: 0, bufT: Infinity, peakY: y };
}

/** Hard-ground a mover at `y` (teleports, corrections, seat snaps): kills any
 *  in-flight arc and both input windows. */
export function groundVert(s: VertState, y: number): void {
  s.y = y; s.vy = 0; s.air = false; s.coyoteT = 0; s.bufT = Infinity; s.peakY = y;
}

export type VertEvents = {
  /** A jump launched this step. */
  jumped: boolean;
  /** The mover touched down this step; `fallDist` = apex → landing surface. */
  landed: null | { fallDist: number };
};

/**
 * Advance one mover's vertical state one frame/tick. The caller has already
 * resolved the horizontal step (colliders/shore/bounds) — (x,z) is where the
 * mover IS; this owns only height. `jumpPressed` must be the press EDGE (true
 * only on the frame the key went down): buffering lives here, so a held key
 * still yields exactly one jump.
 */
export function stepVertical(
  s: VertState,
  x: number,
  z: number,
  jumpPressed: boolean,
  dt: number,
  stand: (x: number, z: number, refY: number) => number | null,
  t: VertTuning = V,
): VertEvents {
  const ev: VertEvents = { jumped: false, landed: null };
  s.bufT = jumpPressed ? 0 : s.bufT + dt;

  if (!s.air) {
    const g = stand(x, z, s.y);
    if (g !== null) {
      const dy = g - s.y;
      if (dy > 0) {
        s.y += Math.min(dy, t.stepUp); // stairs/lips: instant, reach-clamped
      } else if (-dy > t.dropFreefall) {
        s.air = true; s.vy = 0; s.peakY = s.y; // a real drop: go ballistic
      } else if (dy < 0) {
        s.y += dy * Math.min(1, dt * t.glideRate); // small drop: the classic glide
      }
      if (!s.air) s.coyoteT = 0;
    }
    // g === null (cliff face / inside a wall): hold height, stay grounded —
    // see the contract note in the header.
  }

  // Jump: a buffered press fires while grounded or within the coyote window.
  // Consumes both windows, so one press = one jump and no mid-air relaunch.
  if (s.bufT <= t.jumpBuffer && (!s.air || s.coyoteT <= t.coyote)) {
    s.vy = t.jumpVel;
    s.air = true;
    s.peakY = s.y;
    s.bufT = Infinity;
    s.coyoteT = Infinity;
    ev.jumped = true;
  }

  if (s.air) {
    s.coyoteT += dt;
    const preY = s.y;
    s.vy = Math.max(s.vy - t.gravity * dt, -t.maxFallVy);
    s.y += s.vy * dt;
    if (s.y > s.peakY) s.peakY = s.y;
    // Land only while descending: an ascending arc passing a platform's height
    // from below must not snap onto it. The reach reference is the step's START
    // height (a sweep): a terminal-velocity frame moves up to maxFallVy·dt
    // (~2u), more than stepUp — querying at the post-step y would put a thin
    // platform crossed mid-step above reach and tunnel straight through it.
    if (s.vy <= 0) {
      const land = stand(x, z, preY);
      if (land !== null && s.y <= land) {
        s.y = land;
        s.vy = 0;
        s.air = false;
        s.coyoteT = 0;
        ev.landed = { fallDist: Math.max(0, s.peakY - land) };
      }
    }
  }

  return ev;
}

/** Fall damage for a landing: 0 within safeFall, then dmgPerUnit per unit past
 *  it, rounded to whole HP. The server applies this (never the client). */
export function fallDamage(fallDist: number, t: VertTuning = V): number {
  return Math.max(0, Math.round((fallDist - t.safeFall) * t.dmgPerUnit));
}
